import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Clock, Navigation, Calendar } from "lucide-react";

const PricingSection = () => {
  const pricingOptions = [
    {
      icon: <Navigation className="w-10 h-10" />,
      title: "Point-to-Point Service",
      priceRange: "$85 - $150",
      description: "Direct transportation between two locations",
      features: [
        "Flat rate pricing",
        "No hidden fees",
        "Flight tracking included",
        "Meet & greet service",
        "Professional chauffeur"
      ]
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: "Hourly Service",
      priceRange: "$150 - $200/hour",
      description: "Reserve by the hour for multiple stops",
      features: [
        "3-hour minimum",
        "Flexible scheduling",
        "Wait time included",
        "Multiple destinations",
        "Premium vehicles"
      ]
    },
    {
      icon: <Calendar className="w-10 h-10" />,
      title: "Special Events",
      priceRange: "Custom Pricing",
      description: "Weddings, proms, and special occasions",
      features: [
        "Tailored packages",
        "Vehicle decorations",
        "Extended service hours",
        "Multiple vehicle coordination",
        "Dedicated event support"
      ]
    }
  ];

  return (
    <section className="py-20 bg-secondary">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-primary text-sm font-semibold tracking-wider uppercase">
            Transparent Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground">
            Luxury Car Service San Antonio Rates
          </h2>
          <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
            Competitive pricing for premium black car service San Antonio. No hidden fees, just honest rates for exceptional service.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {pricingOptions.map((option, index) => (
            <Card key={index} className="card-luxury hover:scale-105 transition-transform duration-300">
              <CardContent className="p-8 space-y-6">
                <div className="text-primary flex justify-center">{option.icon}</div>
                <div className="text-center">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-2">{option.title}</h3>
                  <p className="text-3xl font-bold text-primary mb-2">{option.priceRange}</p>
                  <p className="text-muted-foreground text-sm">{option.description}</p>
                </div>
                <div className="space-y-3 pt-4 border-t border-border">
                  {option.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="card-luxury max-w-4xl mx-auto border-2 border-primary/20">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif font-bold text-foreground mb-4 text-center">
              Sample Pricing Examples
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <p className="font-semibold text-foreground">SAT Airport to Downtown</p>
                <p className="text-2xl text-primary font-bold">$85 - $100</p>
                <p className="text-sm text-muted-foreground">Sedan | One-way</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-foreground">San Antonio to Austin</p>
                <p className="text-2xl text-primary font-bold">$474</p>
                <p className="text-sm text-muted-foreground">SUV | One-way (Gratuity Included)</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-foreground">4-Hour City Tour</p>
                <p className="text-2xl text-primary font-bold">$540 - $740</p>
                <p className="text-sm text-muted-foreground">4 hours minimum</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-foreground">Wedding Day Service</p>
                <p className="text-2xl text-primary font-bold">$900+</p>
                <p className="text-sm text-muted-foreground">8-hour package</p>
              </div>
            </div>
            <div className="text-center pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">
                All rates include professional chauffeur, fuel, tolls, and taxes. Gratuity not included.
              </p>
              <Button className="btn-luxury" asChild>
                <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                  Get Custom Quote <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-muted-foreground text-sm max-w-3xl mx-auto">
            * Rates may vary based on vehicle selection, distance, time of day, and special requirements. 
            Holiday and peak season surcharges may apply. Contact us for an exact quote tailored to your needs.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
